import { useState } from "react";
import { Link } from "react-router-dom";
import ReactECharts from "echarts-for-react";

import {
  AlertHorizon,
  AlertSide,
  Position,
  PositionOrder,
  useHindsightPositions,
  useHindsightPositionStats,
} from "../../api/alerts";
import { echarts, PALETTE, baseAxis, baseTooltip } from "../../charts/echarts";

type HorizonChoice = AlertHorizon | "all";
type SideChoice = AlertSide | "both";

const HORIZON_CHOICES: { id: HorizonChoice; label: string }[] = [
  { id: "all",      label: "All" },
  { id: "intraday", label: "Intraday" },
  { id: "short",    label: "Short" },
  { id: "medium",   label: "Medium" },
  { id: "long",     label: "Long" },
];

const SIDE_CHOICES: { id: SideChoice; label: string }[] = [
  { id: "both",    label: "Both" },
  { id: "bullish", label: "Buy" },
  { id: "bearish", label: "Sell-short" },
];

function pctFromDecimal(v: number | null): string {
  if (v == null) return "—";
  const p = v * 100;
  const sign = p >= 0 ? "+" : "";
  return `${sign}${p.toFixed(2)}%`;
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 font-mono text-2xl tabular-nums text-slate-100">{value}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-500">{hint}</div>}
    </div>
  );
}

function ExcessHistogram({ positions }: { positions: Position[] }) {
  const values = positions
    .map((p) => p.excess_return)
    .filter((v): v is number => v != null)
    .map((v) => v * 100);
  if (values.length === 0) return null;

  // 12 buckets across observed range, with sensible defaults if the spread
  // is tiny so the chart never collapses to a single bar.
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(max - min, 2);
  const lo = Math.floor((min - span * 0.05) / 2) * 2;
  const hi = Math.ceil((max + span * 0.05) / 2) * 2;
  const buckets = 12;
  const width = (hi - lo) / buckets;
  const counts = new Array<number>(buckets).fill(0);
  for (const v of values) {
    let idx = Math.floor((v - lo) / width);
    if (idx < 0) idx = 0;
    if (idx >= buckets) idx = buckets - 1;
    counts[idx]++;
  }
  const labels = counts.map((_, i) => {
    const start = (lo + i * width).toFixed(1);
    const end = (lo + (i + 1) * width).toFixed(1);
    return `${start}–${end}`;
  });
  const colors = counts.map((_, i) => {
    const mid = lo + (i + 0.5) * width;
    return mid >= 0 ? PALETTE.bull : PALETTE.bear;
  });

  const option = {
    backgroundColor: "transparent",
    animation: false,
    tooltip: {
      ...baseTooltip,
      formatter: (p: any) => {
        const i = p[0].dataIndex;
        return `excess ${labels[i]}%<br/>n=${counts[i]}`;
      },
    },
    grid: { left: 36, right: 16, top: 16, bottom: 50 },
    xAxis: {
      ...baseAxis,
      type: "category",
      data: labels,
      axisLabel: { ...baseAxis.axisLabel, rotate: 30 },
    },
    yAxis: {
      ...baseAxis,
      type: "value",
      minInterval: 1,
    },
    series: [
      {
        type: "bar",
        data: counts.map((v, i) => ({ value: v, itemStyle: { color: colors[i] } })),
        barWidth: "80%",
      },
    ],
  } satisfies echarts.EChartsCoreOption;
  return (
    <ReactECharts option={option} style={{ height: 200, width: "100%" }} echarts={echarts} notMerge />
  );
}

function PairsTable({ positions }: { positions: Position[] }) {
  if (positions.length === 0) return null;
  return (
    <div className="overflow-hidden rounded-xl border border-slate-800">
      <table className="w-full text-left text-[12px]">
        <thead className="bg-slate-900/60 text-[10px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2">Symbol</th>
            <th className="px-3 py-2">Horizon · side</th>
            <th className="px-3 py-2 text-right">Opened</th>
            <th className="px-3 py-2 text-right">Closed</th>
            <th className="px-3 py-2 text-right">Hold (d)</th>
            <th className="px-3 py-2 text-right">Raw</th>
            <th className="px-3 py-2 text-right">vs SPY</th>
            <th className="px-3 py-2">Result</th>
            <th className="px-3 py-2 text-right">Reason</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {positions.map((p) => {
            const openD = p.opened_at ? new Date(p.opened_at) : null;
            const closeD = p.closed_at ? new Date(p.closed_at) : null;
            const holdDays =
              openD && closeD
                ? Math.max(
                    0,
                    Math.round((closeD.getTime() - openD.getTime()) / 86_400_000),
                  )
                : null;
            const rawCls =
              (p.raw_return ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400";
            const exCls =
              (p.excess_return ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400";
            return (
              <tr key={p.id} className="bg-slate-950">
                <td className="px-3 py-2">
                  <Link
                    to={`/s/${encodeURIComponent(p.yahoo_symbol)}`}
                    className="font-mono text-slate-100 hover:text-sky-300"
                  >
                    {p.ticker}
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-300">
                  {p.horizon}{" "}
                  <span className="text-[10px] text-slate-500">· {p.side}</span>
                </td>
                <td className="px-3 py-2 text-right text-[11px] text-slate-400">
                  {openD ? openD.toLocaleDateString() : "—"}{" "}
                  <span className="text-slate-500">
                    @ {p.open_score.toFixed(0)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right text-[11px] text-slate-400">
                  {closeD ? closeD.toLocaleDateString() : "—"}{" "}
                  <span className="text-slate-500">
                    @ {p.close_score != null ? p.close_score.toFixed(0) : "—"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-300">
                  {holdDays ?? "—"}
                </td>
                <td
                  className={`px-3 py-2 text-right font-mono tabular-nums ${rawCls}`}
                >
                  {pctFromDecimal(p.raw_return)}
                </td>
                <td
                  className={`px-3 py-2 text-right font-mono tabular-nums ${exCls}`}
                >
                  {pctFromDecimal(p.excess_return)}
                </td>
                <td className="px-3 py-2">
                  {p.hit == null ? (
                    <span className="rounded bg-slate-800 px-2 py-0.5 text-[10px] text-slate-500">
                      —
                    </span>
                  ) : p.hit ? (
                    <span className="rounded bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
                      HIT
                    </span>
                  ) : (
                    <span className="rounded bg-rose-500/20 px-2 py-0.5 text-[10px] font-medium text-rose-300">
                      MISS
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right text-[10px] text-slate-500">
                  {p.close_reason ?? "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function StrategyTab() {
  const [horizon, setHorizon] = useState<HorizonChoice>("all");
  const [side, setSide] = useState<SideChoice>("both");
  const [order, setOrder] = useState<PositionOrder>("recent");

  const stats = useHindsightPositionStats({ horizon, side });
  const positions = useHindsightPositions({ horizon, side, order, limit: 50 });

  const nClosed = stats.data?.n_closed ?? 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-md border border-slate-700 bg-slate-900 p-0.5 text-[11px]">
          {HORIZON_CHOICES.map((h) => (
            <button
              key={h.id}
              onClick={() => setHorizon(h.id)}
              className={`rounded px-2 py-1 ${horizon === h.id ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"}`}
            >
              {h.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-md border border-slate-700 bg-slate-900 p-0.5 text-[11px]">
          {SIDE_CHOICES.map((s) => (
            <button
              key={s.id}
              onClick={() => setSide(s.id)}
              className={`rounded px-2 py-1 ${side === s.id ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-[11px] leading-relaxed text-slate-500">
        Round-trip pairs from your banded score alerts: each row is a buy that
        later closed via exit signal, timeout, or manual close. Open positions
        live in <Link to="/settings" className="text-sky-400 hover:underline">Settings → Score alerts</Link>.
      </p>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Closed pairs"
          value={nClosed.toLocaleString()}
          hint={
            stats.data?.n_open != null
              ? `${stats.data.n_open} open`
              : undefined
          }
        />
        <StatCard
          label="Win rate"
          value={
            stats.data?.win_rate != null
              ? `${Math.round(stats.data.win_rate * 100)}%`
              : "—"
          }
          hint="excess return > 0"
        />
        <StatCard
          label="Avg raw return"
          value={pctFromDecimal(stats.data?.avg_raw ?? null)}
        />
        <StatCard
          label="Avg vs SPY"
          value={pctFromDecimal(stats.data?.avg_excess ?? null)}
        />
      </div>

      {nClosed > 0 && positions.data && positions.data.length > 0 && (
        <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
          <header className="mb-2 text-sm font-medium text-slate-300">
            Excess-return distribution
          </header>
          <ExcessHistogram positions={positions.data} />
        </section>
      )}

      <section className="space-y-2">
        <header className="flex items-center justify-between">
          <div className="text-sm font-medium text-slate-300">Recent pairs</div>
          <div className="flex gap-1 rounded-md border border-slate-700 bg-slate-900 p-0.5 text-[11px]">
            {(["recent", "best", "worst"] as PositionOrder[]).map((o) => (
              <button
                key={o}
                onClick={() => setOrder(o)}
                className={`rounded px-2 py-1 ${order === o ? "bg-slate-700 text-slate-100" : "text-slate-400 hover:text-slate-200"}`}
              >
                {o}
              </button>
            ))}
          </div>
        </header>
        {positions.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (positions.data?.length ?? 0) === 0 ? (
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 text-sm text-slate-500">
            No closed pairs yet. Once a buy alert fires and later closes
            (exit signal or timeout), it&apos;ll appear here.
          </div>
        ) : (
          <PairsTable positions={positions.data ?? []} />
        )}
      </section>
    </div>
  );
}
